import * as THREE from "three";
import type { Rng } from "./rng";

export type Vec3 = readonly [number, number, number];

const cross = (a: Vec3, b: Vec3, c: Vec3): Vec3 => {
  const ux = b[0] - a[0];
  const uy = b[1] - a[1];
  const uz = b[2] - a[2];
  const vx = c[0] - a[0];
  const vy = c[1] - a[1];
  const vz = c[2] - a[2];
  return [uy * vz - uz * vy, uz * vx - ux * vz, ux * vy - uy * vx];
};

const dot = (a: Vec3, b: Vec3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

export class SolidBuilder {
  private readonly positions: number[] = [];

  vertex(v: Vec3): this {
    this.positions.push(v[0], v[1], v[2]);
    return this;
  }

  triangle(a: Vec3, b: Vec3, c: Vec3, outward?: Vec3): this {
    const flip = outward !== undefined && dot(cross(a, b, c), outward) < 0;
    if (flip) return this.vertex(a).vertex(c).vertex(b);
    return this.vertex(a).vertex(b).vertex(c);
  }

  quad(a: Vec3, b: Vec3, c: Vec3, d: Vec3, outward?: Vec3): this {
    const flip = outward !== undefined && dot(cross(a, b, c), outward) < 0;
    if (flip) return this.triangle(a, d, c).triangle(a, c, b);
    return this.triangle(a, b, c).triangle(a, c, d);
  }

  build(): THREE.BufferGeometry {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(this.positions, 3));
    geometry.computeVertexNormals();
    return geometry;
  }
}

const UP = new THREE.Vector3(0, 1, 0);

export function segmentGeometry(
  from: THREE.Vector3,
  to: THREE.Vector3,
  radiusFrom: number,
  radiusTo: number,
  sides: number,
): THREE.BufferGeometry {
  const direction = new THREE.Vector3().subVectors(to, from);
  const length = Math.max(direction.length(), 1e-4);
  const geometry = new THREE.CylinderGeometry(radiusTo, radiusFrom, length, sides, 1, true);
  const quaternion = new THREE.Quaternion().setFromUnitVectors(
    UP,
    direction.clone().normalize(),
  );
  const centre = new THREE.Vector3().addVectors(from, to).multiplyScalar(0.5);
  geometry.applyMatrix4(
    new THREE.Matrix4().compose(centre, quaternion, new THREE.Vector3(1, 1, 1)),
  );
  return geometry;
}

export function blobGeometry(
  radius: number,
  detail: number,
  jitter: number,
  rng: Rng,
): THREE.BufferGeometry {
  const geometry = new THREE.IcosahedronGeometry(radius, detail);
  const position = geometry.getAttribute("position") as THREE.BufferAttribute;
  const scale = new THREE.Vector3();

  for (let i = 0; i < position.count; i++) {
    scale.fromBufferAttribute(position, i);
    const length = scale.length();
    const push = 1 + (rng.next() - 0.5) * 2 * jitter;
    scale.multiplyScalar((length * push) / length);
    scale.y *= 1 - jitter * 0.35;
    position.setXYZ(i, scale.x, scale.y, scale.z);
  }

  geometry.computeVertexNormals();
  return geometry;
}

export function jitterGeometry(
  geometry: THREE.BufferGeometry,
  amount: number,
  rng: Rng,
): THREE.BufferGeometry {
  const position = geometry.getAttribute("position") as THREE.BufferAttribute;
  for (let i = 0; i < position.count; i++) {
    position.setXYZ(
      i,
      position.getX(i) + (rng.next() - 0.5) * amount,
      position.getY(i) + (rng.next() - 0.5) * amount,
      position.getZ(i) + (rng.next() - 0.5) * amount,
    );
  }
  position.needsUpdate = true;
  geometry.computeVertexNormals();
  return geometry;
}

export function mesh(
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  x = 0,
  y = 0,
  z = 0,
): THREE.Mesh {
  const solid = new THREE.Mesh(geometry, material);
  solid.position.set(x, y, z);
  solid.castShadow = true;
  solid.receiveShadow = true;
  return solid;
}

export function group(children: readonly THREE.Object3D[] = []): THREE.Group {
  const wrapper = new THREE.Group();
  for (const child of children) wrapper.add(child);
  return wrapper;
}
